// AtriumTopBar.tsx — Atrium 62px top bar (Sprint 82, ADR-082; Sprint 88A actions).
// ----------------------------------------------------------------------------
// Breadcrumb (Section / **Page**) on the left; on the right a search field, an
// optional super-admin/admin PROPERTY SWITCHER, the auth-aware RoleSwitch chip,
// a notification bell, and the AVATAR MENU (My Profile / Account Settings /
// Sign Out). Visual spec mirrors prototype `.wtop` (theme2.jsx ~58-66,
// nav2.jsx WTop).
//
// Sprint 88A (shell-action gaps from the 2026-06-04 full E2E):
//   - M1: avatar is now a real <Dropdown> button → Profile / Settings / Sign Out.
//   - H2: super-admin / multi-condo admin gets the top-bar property switcher
//         (parity with legacy DashboardHeader; same `onSwitchCondo` re-scope).
//   - H3: Settings reachable from the avatar menu (also still pinned in NavRail).
// All additive + reversible — props are optional, legacy shell untouched.
import React from 'react';
import { Dropdown, Select, type MenuProps } from 'antd';
import {
  SearchOutlined, ProfileOutlined,
  LogoutOutlined, DownOutlined, BankOutlined,
} from '@ant-design/icons';
import { RoleSwitch } from './RoleSwitch';
import { AtriumNotificationBell } from './AtriumNotificationBell';
import type { DashboardComponent } from '../dashboard/menuConfig';

/** Minimal condo shape the switcher needs (matches authStore.Condo). */
export interface AtriumCondoOption {
  id:   string;
  name: string;
}

/**
 * Minimal nav-destination shape the quick-nav search needs. Sourced from the
 * already role/feature-filtered menu items (MenuItem) the shell holds, so the
 * palette only ever offers pages the user can actually reach.
 */
export interface AtriumNavTarget {
  key:     DashboardComponent;
  label:   string;
  section: string;
}

export interface AtriumTopBarProps {
  sectionLabel: string;
  pageLabel:    string;
  role:         string | undefined;
  avatar:       string;
  // Sprint 90 — the bell is now a self-contained notifications panel
  // (AtriumNotificationBell) that reads condoStore + shows an unread badge.
  // `hasAlerts`/`onBell` are retained for backward compat but no longer drive
  // the bell; `onViewAllNotifications` lets the panel deep-link to the full
  // Notification Center (Communications hub).
  hasAlerts?:   boolean;
  onBell?:      () => void;
  onViewAllNotifications?: () => void;
  // Sprint 88A — avatar menu actions (reuse MainDashboard's existing handlers).
  onProfile?:   () => void;
  onLogout?:    () => void;
  // Sprint 88A — property switcher (parity with legacy DashboardHeader).
  availableCondos?: AtriumCondoOption[];
  currentCondo?:    AtriumCondoOption | null;
  onSwitchCondo?:   (condoId: string) => void;
  // F4 — global quick-nav: client-side command palette over the user's
  // (already filtered) nav destinations. Selecting one navigates via the
  // shell's existing onSelect. No backend search call. Both optional so the
  // legacy/standalone shell stays untouched when not wired.
  navTargets?:  AtriumNavTarget[];
  onNavTarget?: (key: DashboardComponent) => void;
}

const topStyle: React.CSSProperties = {
  height: 'var(--top-bar)', flex: 'none', display: 'flex', alignItems: 'center',
  gap: 16, padding: '0 26px', borderBottom: '1px solid var(--line)',
  background: 'var(--card)',
};

const searchStyle: React.CSSProperties = {
  width: 300, display: 'flex', alignItems: 'center', gap: 9,
  background: 'var(--bg)', border: '1px solid var(--line)', borderRadius: 10,
  padding: '9px 13px', color: 'var(--mut)', fontSize: 13, cursor: 'text',
  fontFamily: 'inherit', textAlign: 'left',
};

const avatarBtnStyle: React.CSSProperties = {
  width: 38, height: 38, borderRadius: '50%', background: 'var(--blue-s)',
  color: 'var(--blue)', display: 'flex', alignItems: 'center',
  justifyContent: 'center', fontWeight: 800, fontSize: 13, cursor: 'pointer',
  border: '1px solid var(--line2)', fontFamily: 'inherit', padding: 0,
};

const condoNameStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 7, height: 38,
  padding: '0 13px', borderRadius: 10, background: 'var(--card)',
  border: '1px solid var(--line2)', color: 'var(--ink)',
  fontSize: 13, fontWeight: 700, maxWidth: 240,
};

export const AtriumTopBar: React.FC<AtriumTopBarProps> = ({
  sectionLabel, pageLabel, role, avatar, onViewAllNotifications,
  onProfile, onLogout,
  availableCondos, currentCondo, onSwitchCondo,
  navTargets, onNavTarget,
}) => {
  // ── Property switcher gating (parity with legacy DashboardHeader, Sprint 81):
  //    any admin-tier user (admin OR super-admin) with ≥1 available condo gets
  //    the switcher. Single-condo owners keep static text. Hooks are always
  //    called (none here) so this stays render-only / order-stable.
  const isAdminTier =
    role === 'super_admin_condo' ||
    role === 'superadmin' ||
    role === 'super_admin' ||
    role === 'admin';
  const condos = availableCondos ?? [];
  const showSwitcher = isAdminTier && condos.length > 1 && !!onSwitchCondo;

  // ── F4 quick-nav: a client-side command palette over the user's filtered nav
  //    destinations. Functional only when the shell wires both the targets and
  //    the navigate handler; otherwise we render a disabled placeholder so the
  //    control is never a silent dead button. (No backend search.)
  const targets = navTargets ?? [];
  const quickNavEnabled = targets.length > 0 && !!onNavTarget;

  // ── Avatar menu (M1/H3). AntD Dropdown handles keyboard + ARIA on the menu;
  //    the trigger is a real <button> with an aria-label.
  const avatarMenu: MenuProps['items'] = [
    { key: 'profile',  icon: <ProfileOutlined />, label: 'My Profile',       onClick: () => onProfile?.()  },
    { key: 'divider',  type: 'divider' },
    { key: 'logout',   icon: <LogoutOutlined />,  label: 'Sign Out',         onClick: () => onLogout?.()   },
  ];

  return (
    // Banner landmark (WCAG 1.3.1).
    <header style={topStyle}>
      {/* Breadcrumb (WCAG 2.4.8). */}
      <nav aria-label="Breadcrumb" style={{ fontSize: 13, color: 'var(--mut)', fontWeight: 600 }}>
        {sectionLabel}
        <span style={{ margin: '0 7px', color: 'var(--mut2)' }} aria-hidden="true">/</span>
        <b aria-current="page" style={{ color: 'var(--ink)', fontWeight: 800, fontSize: 15 }}>{pageLabel}</b>
      </nav>

      <div style={{ flex: 1 }} />

      {quickNavEnabled ? (
        // Functional quick-nav: type to filter the user's reachable pages;
        // selecting one navigates via the shell's existing onSelect. Purely
        // client-side over `navTargets`. Cleared after each jump so it reads as
        // a search box, not a current-value picker.
        <Select
          showSearch
          value={null}
          aria-label="Search pages"
          placeholder="Search pages…"
          prefix={<SearchOutlined aria-hidden="true" style={{ color: 'var(--mut)' }} />}
          style={{ width: 300 }}
          optionFilterProp="searchText"
          onChange={(key) => { if (key) onNavTarget?.(key as DashboardComponent); }}
          notFoundContent="No matching pages"
          options={targets.map(t => ({
            value: t.key,
            // searchText drives filtering across both label + section.
            searchText: `${t.label} ${t.section}`,
            label: (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontWeight: 600 }}>{t.label}</span>
                <span style={{ color: 'var(--mut)', fontSize: 12 }}>{t.section}</span>
              </span>
            ),
          }))}
        />
      ) : (
        // Fallback: never a silent dead button — a clearly inert placeholder.
        <span
          aria-label="Search (coming soon)"
          title="Search is coming soon"
          aria-disabled="true"
          style={{ ...searchStyle, cursor: 'not-allowed', opacity: 0.6 }}
        >
          <SearchOutlined style={{ fontSize: 16 }} aria-hidden="true" />
          <span>Search (coming soon)</span>
        </span>
      )}

      {/* Property switcher (H2) — multi-condo admin/super-admin only. Selecting a
          condo calls the SAME re-scope handler the legacy switcher used. */}
      {showSwitcher ? (
        <Select
          value={currentCondo?.id}
          onChange={(id) => onSwitchCondo?.(id)}
          aria-label="Switch property"
          placeholder="Select property"
          style={{ width: 220 }}
          suffixIcon={<DownOutlined />}
          options={condos.map(c => ({
            value: c.id,
            label: (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
                <BankOutlined aria-hidden="true" />
                <span>{c.name}</span>
              </span>
            ),
          }))}
        />
      ) : currentCondo ? (
        // Single-condo owner: static current-property label (no switcher).
        <span style={condoNameStyle} title={currentCondo.name}>
          <BankOutlined aria-hidden="true" style={{ color: 'var(--mut)' }} />
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {currentCondo.name}
          </span>
        </span>
      ) : null}

      <RoleSwitch role={role} />

      {/* Notifications bell (Sprint 90) — self-contained panel that reuses the
          NotificationCenter data (condoStore notifications, condo-scoped) and
          shows an unread (active) count badge. "View all" deep-links to the
          full Notification Center via onViewAllNotifications. */}
      <AtriumNotificationBell onViewAll={onViewAllNotifications} />

      {/* Avatar menu (M1) — real button trigger; AntD Dropdown is keyboard-operable. */}
      <Dropdown menu={{ items: avatarMenu }} placement="bottomRight" trigger={['click']}>
        <button type="button" aria-label="Account menu" title="Account" style={avatarBtnStyle}>
          {avatar}
        </button>
      </Dropdown>
    </header>
  );
};
